import classNames from 'classnames';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEffectEvent } from 'use-effect-event';
import { useDrag } from '@use-gesture/react';
import SVGIcon from '../components/SVGIcon';
import DraggingAutoScroll from '../editor/utils/DraggingAutoScroll';
import findClosestScrollingContainer from '../editor/utils/findClosestScrollingContainer';
import dragIcon from '../icons/non-prefilled/16x16/drag.svg';
import * as styles from './NewReorderableList.module.scss';

const GROUP_ATTRIBUTE_NAME = 'data-reorder-group-id';
const ROW_ATTRIBUTE_NAME = 'data-reorder-row';

type DragState = {
  sourceIndex: number;
  destinationIndex: number; // visual destination index used for indicators
  insertionIndex: number; // actual insertion index on drop
};

type InnerContextType = {
  isDisabled?: boolean;
  dragState: DragState | null;
  setDragState: (state: DragState | null) => void;
  onCommitReorder?: (sourceIndex: number, insertionIndex: number) => void;
};

const InnerContext = React.createContext<InnerContextType>({
  isDisabled: false,
  dragState: null,
  setDragState: () => {},
});

interface NewReorderableListProps {
  disabled?: boolean;
  children: React.ReactNode;
  onMove?: (sourceIndex: number, destinationIndex: number) => void;
}

const NewReorderableList = ({
  disabled,
  children,
  onMove,
}: NewReorderableListProps) => {
  const [dragState, setDragState] = useState<DragState | null>(null);

  const onMoveEvent = useEffectEvent(onMove ?? (() => {}));
  const onCommitReorder = useCallback(
    (sourceIndex: number, insertionIndex: number) => {
      if (insertionIndex !== sourceIndex) {
        onMoveEvent(sourceIndex, insertionIndex);
      }
    },
    [],
  );

  const value = useMemo(
    () => ({
      isDisabled: disabled,
      dragState,
      setDragState,
      onCommitReorder,
    }),
    [disabled, dragState, onCommitReorder],
  );

  return (
    <InnerContext.Provider value={value}>
      <div className={styles.container} {...{ [GROUP_ATTRIBUTE_NAME]: true }}>
        {children}
      </div>
    </InnerContext.Provider>
  );
};

const DragHandle = (
  props: Omit<React.HTMLAttributes<HTMLDivElement>, 'className'>,
) => (
  <div className={styles.dragHandle} {...props}>
    <SVGIcon className={styles.dragIcon}>{dragIcon}</SVGIcon>
  </div>
);

type DraggableSnapshot = { isDragging: boolean };

interface RowProps {
  children: (
    snapshot: DraggableSnapshot,
    dragState: DragState | null,
  ) => React.ReactNode;
  index: number;
  className?: string;
}

const Row = ({ children, index, className }: RowProps) => {
  const { isDisabled, dragState, setDragState, onCommitReorder } =
    useContext(InnerContext);

  const rowRef = useRef<HTMLDivElement | null>(null);
  const pointerOffsetYRef = useRef<number>(0);
  const draggingAutoScrollRef = useRef<DraggingAutoScroll | null>(null);
  const scrollingContainerRef = useRef<HTMLElement | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [dragPreviewBounds, setDragPreviewBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  const bindDrag = useDrag(
    ({ first, last, event, xy: [, y] }) => {
      if (isDisabled) return;

      if (first) {
        setIsDragging(true);
        const rect = rowRef.current?.getBoundingClientRect();
        if (rect) {
          pointerOffsetYRef.current = y - rect.top;
          setDragPreviewBounds({
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
          });
        } else {
          pointerOffsetYRef.current = 0;
          setDragPreviewBounds(null);
        }

        const scrollingContainer = findClosestScrollingContainer(
          rowRef.current,
        );
        if (scrollingContainer) {
          scrollingContainerRef.current = scrollingContainer;
          draggingAutoScrollRef.current = new DraggingAutoScroll(
            scrollingContainer,
          );
          draggingAutoScrollRef.current.start();
        }
        return;
      }

      if (last) {
        setIsDragging(false);
        draggingAutoScrollRef.current?.stop();
        if (dragState) {
          onCommitReorder?.(index, dragState.destinationIndex);
        }
        setDragState(null);
        return;
      }

      const groupElement =
        event.currentTarget instanceof Element
          ? event.currentTarget.closest(`[${GROUP_ATTRIBUTE_NAME}]`)
          : null;
      const rows = Array.from(
        groupElement?.querySelectorAll(`[${ROW_ATTRIBUTE_NAME}]`) ?? [],
      );
      if (rows.length > 0) {
        setDragPreviewBounds((previous) =>
          previous
            ? { ...previous, top: y - pointerOffsetYRef.current }
            : previous,
        );
        const midpoints = rows.map((el) => {
          const rect = el.getBoundingClientRect();
          return rect.top + rect.height / 2;
        });
        let insertionIndex = midpoints.findIndex((mid) => y < mid);
        if (insertionIndex === -1) insertionIndex = rows.length;
        const sourceIndex = index;
        const destinationIndex = Math.max(
          0,
          Math.min(
            rows.length - 1,
            insertionIndex - (sourceIndex < insertionIndex ? 1 : 0),
          ),
        );
        setDragState({ sourceIndex, destinationIndex, insertionIndex });
      }

      draggingAutoScrollRef.current?.setMouseYOffset(y);
    },
    { enabled: !isDisabled, threshold: 5 },
  );

  useEffect(() => {
    return () => {
      draggingAutoScrollRef.current?.stop();
    };
  }, []);

  const rowClassName = classNames(styles.rowContainer, className);
  const snapshot: DraggableSnapshot = { isDragging };
  const content = (
    <div className={styles.content}>{children(snapshot, dragState)}</div>
  );

  return (
    <>
      <div
        ref={rowRef}
        className={classNames(rowClassName, {
          [styles.isDragging]: isDragging,
          [styles.hasActiveDrag]: dragState,
        })}
        {...{ [ROW_ATTRIBUTE_NAME]: true }}
      >
        {!isDisabled && <DragHandle {...bindDrag()} />}
        {content}
      </div>
      {isDragging && dragPreviewBounds && (
        <div
          className={classNames(rowClassName, styles.dragPreview)}
          style={{
            left: `${dragPreviewBounds.left}px`,
            top: `${dragPreviewBounds.top}px`,
            width: `${dragPreviewBounds.width}px`,
          }}
        >
          <DragHandle />
          {content}
        </div>
      )}
    </>
  );
};

NewReorderableList.Row = Row;

interface DragIndicatorProps {
  children: React.ReactNode;
  className?: string;
  index: number;
}

const DragIndicator = ({ className, children, index }: DragIndicatorProps) => {
  const { dragState } = useContext(InnerContext);
  return (
    <span
      className={classNames(styles.dragIndicator, className, {
        [styles.topActive]:
          dragState?.destinationIndex === index &&
          dragState.sourceIndex > dragState.destinationIndex,
        [styles.bottomActive]:
          dragState?.destinationIndex === index &&
          dragState.sourceIndex < dragState.destinationIndex,
      })}
    >
      <span className={styles.dragIndicatorTop} />
      {children}
      <span className={styles.dragIndicatorBottom} />
    </span>
  );
};

NewReorderableList.DragIndicator = DragIndicator;

export default NewReorderableList;
